import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type MeResponse } from './api';
import { completeSignIn, isSignedIn, signIn, signOutFederated } from './auth';
import { AdminView } from './views/AdminView';
import { PrivacyPolicy } from './views/PrivacyPolicy';
import { Suggestions } from './views/Suggestions';
import { CalendarView } from './views/CalendarView';
import { LogView } from './views/LogView';
import { AssistantView } from './views/AssistantView';
import { ExportView } from './views/ExportView';
import { SettingsView } from './views/SettingsView';
import { Gate } from './views/Gate';

type Tab = 'week' | 'log' | 'assistant' | 'settings' | 'export' | 'admin';

/**
 * Shell and routing. The router is deliberately tiny — this app has one authenticated
 * surface with four tabs plus two unauthenticated entry routes, and a routing library would
 * be more code than the thing it replaces.
 */
export function App() {
  const [me, setMe] = useState<MeResponse | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  // The week is what a parent opens the app to see. Export is a thing you do a few
  // times a year, so it sits last rather than competing for attention daily.
  const [tab, setTab] = useState<Tab>('week');

  const refresh = useCallback(async () => {
    try {
      setMe(await api.me());
      setError(undefined);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const path = window.location.pathname;

      if (path === '/auth/callback') {
        try {
          const next = await completeSignIn();
          window.history.replaceState({}, '', next);
        } catch (err) {
          setError(messageFor(err));
          setLoading(false);
          return;
        }
      }

      if (!isSignedIn()) {
        setLoading(false);
        return;
      }
      await refresh();
    })();
  }, [refresh]);

  // Readable without signing in: Google's verification review reads it, and anyone deciding
  // whether to connect a calendar should be able to read it before they do.
  if (window.location.pathname === '/privacy') return <PrivacyPolicy />;

  if (loading) {
    return <div className="centered"><p className="muted">Loading…</p></div>;
  }

  if (!isSignedIn()) {
    return (
      <div className="centered">
        <h1>unschool.works</h1>
        <p className="muted">
          A homeschool instruction log that keeps itself. Sign in to see your records.
        </p>
        {error && <div className="notice error">{error}</div>}
        <button className="primary" onClick={() => void signIn()}>
          Sign in with Google
        </button>
      </div>
    );
  }

  if (error && !me) {
    return (
      <div className="centered">
        <div className="notice error">{error}</div>
        <button onClick={() => void signOutFederated()}>Sign out</button>
      </div>
    );
  }

  // Signed in with Google, but not yet a member of any account.
  if (me && !me.member) {
    return <Gate me={me} onJoined={() => void refresh()} />;
  }

  const capExceeded = me?.usage ? me.usage.remainingCents <= 0 : false;

  return (
    <div className="shell">
      <header className="top">
        <h1>unschool.works</h1>
        <span className="muted">{me?.account?.name}</span>
        <span className="spacer" />
        <span className="muted">{me?.identity.email}</span>
        <button onClick={() => void signOutFederated()}>Sign out</button>
      </header>

      {capExceeded && (
        <div className="notice warn">
          This month's AI budget is spent, so recording summaries, the assistant, and voice
          input are paused until {nextMonthName()}. Your log still works — you can add and
          edit entries by hand, and exports are unaffected. Raise the cap in Settings.
        </div>
      )}

      {me && <Suggestions me={me} onChanged={() => void refresh()} onGoToSettings={() => setTab('settings')} />}

      <nav className="tabs">
        {(
          [
            'week',
            'log',
            'assistant',
            'settings',
            'export',
            ...(me?.superadmin ? (['admin'] as const) : []),
          ] as const
        ).map((key) => (
          <button key={key} aria-current={tab === key} onClick={() => setTab(key)}>
            {
              {
                week: 'This week',
                log: 'Log',
                assistant: 'Assistant',
                settings: 'Settings',
                export: 'Export',
                admin: 'Operator',
              }[key]
            }
          </button>
        ))}
      </nav>

      {me && tab === 'week' && <CalendarView me={me} onChanged={() => void refresh()} />}
      {me && tab === 'log' && <LogView me={me} onChanged={() => void refresh()} />}
      {me && tab === 'assistant' && <AssistantView me={me} />}
      {me && tab === 'export' && <ExportView me={me} />}
      {me && tab === 'settings' && <SettingsView me={me} onChanged={() => void refresh()} />}
      {me && tab === 'admin' && me.superadmin && <AdminView />}
    </div>
  );
}

function nextMonthName(): string {
  const next = new Date();
  next.setMonth(next.getMonth() + 1, 1);
  return next.toLocaleString(undefined, { month: 'long' });
}

export function messageFor(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
}
